import { Button, IconButton, Box } from "@mui/material";
import LogisticsPickupRow from "./LogisticsPickupRow";
import LogisticsPickupTabs from "./LogisticsPickupTabs";
import { Add } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { isValidElement, useCallback, useRef } from "react";
import useLogisticsPickups from "hooks/useLogisticsPickups";
import {
  DataGridPro,
  getGridStringOperators,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColDef,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GridRenderCellParams,
  GridRowParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridValueGetterParams,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { Pickup } from "types/pickup";
import PickupStateChip from "components/PickupState";
import useServerSideDataGridParams from "hooks/useServerSideDataGridParams";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { readableDate } from "utils/readableDate";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { PageActions, PageBreadcrumbs, PageSubtitle, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { PagePaperPreamble } from "pages/Layouts/components/PagePaperPreamble";
import { useFeatureFlag } from "hooks/useFeatureFlag";

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton {...({} as any)} />
      <GridToolbarFilterButton {...({} as any)} />
      <GridToolbarDensitySelector {...({} as any)} />
    </GridToolbarContainer>
  );
};

const CustomDetailPanelToggle = (props: Pick<GridRenderCellParams, "id" | "value">) => {
  const { id, value: isExpanded } = props;
  const apiRef = useGridApiContext();
  const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);
  const hasDetail = isValidElement(contentCache[id]);

  return (
    <IconButton size="small" tabIndex={-1} disabled={!hasDetail} aria-label={isExpanded ? "Close" : "Open"}>
      <ExpandMoreIcon
        sx={{
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: (theme) =>
            theme.transitions.create("transform", {
              duration: theme.transitions.duration.shortest,
            }),
        }}
        fontSize="inherit"
      />
    </IconButton>
  );
};

const columns: GridColDef[] = [
  {
    field: "scheduledAt",
    headerName: "Date",
    width: 125,
    sortable: true,
    valueGetter: (params: GridValueGetterParams) => {
      return readableDate(params.row.scheduledAt);
    },
  },
  {
    field: "confirmedTime",
    headerName: "Time",
    width: 90,
    sortable: true,
  },
  {
    field: "orderNumber",
    headerName: "External ID",
    width: 100,
    sortable: true,
  },
  {
    field: "id",
    headerName: "Internal ID",
    width: 100,
    sortable: true,
  },
  {
    field: "pickup_details.agent",
    headerName: "Agent",
    width: 120,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.pickupAgent?.agent?.name;
    },
  },
  {
    field: "pickup_details.itad",
    headerName: "ITAD",
    width: 120,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.pickupItad?.itad?.name;
    },
  },
  {
    field: "pickup_details.coordinator",
    headerName: "Coordinator",
    width: 100,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.revivnCoordinator?.firstName;
    },
  },
  {
    field: "organizations.name",
    headerName: "Company",
    width: 200,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.organization?.name;
    },
  },
  {
    field: "location",
    headerName: "Location",
    minWidth: 450,
    flex: 1,
    sortable: false,
    filterable: false,
    valueGetter: ({ row: { location } }: GridValueGetterParams<Pickup, Pickup>) => {
      return (
        location && `${location.address || ""} ${location.city || ""} ${location.state || ""} ${location.zipcode || ""}`
      );
    },
  },
  {
    field: "state",
    headerName: "State",
    width: 250,
    sortable: true,
    renderCell({ row: { state } }: GridRenderCellParams<Pickup>) {
      return <PickupStateChip pickupState={state} />;
    },
  },
  {
    field: "pickup_details.freighter",
    headerName: "Freighter",
    width: 80,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.freighter?.name;
    },
  },
  {
    field: "rescheduleInfo",
    headerName: "Reschedule Info",
    width: 200,
    sortable: false,
    renderCell: (params: GridRenderCellParams<Pickup>) => {
      const pickup = params.row;

      if (pickup.state !== "reschedule_pending" && !pickup.rescheduledFrom) {
        return "-";
      }

      const rescheduleDetails = [
        pickup.rescheduledFrom && `From: ${readableDate(pickup.rescheduledFrom)}`,
        pickup.rescheduledBy && `By: ${pickup.rescheduledBy.email}`,
        pickup.rescheduledReason && `Reason: ${pickup.rescheduledReason}`,
        pickup.reschedulePendingAt && `Date: ${new Date(pickup.reschedulePendingAt).toLocaleDateString()}`,
      ].filter(Boolean);

      return (
        <Box sx={{ fontSize: "12px", lineHeight: 1.2 }}>
          {rescheduleDetails.map((detail, index) => (
            <div key={index}>{detail}</div>
          ))}
        </Box>
      );
    },
  },
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    renderCell: (params) => <CustomDetailPanelToggle id={params.id} value={params.value} />,
  },
];

const newColumns: GridColDef[] = [
  {
    field: "scheduledAt",
    headerName: "Date",
    width: 125,
    sortable: true,
    valueGetter: (params: GridValueGetterParams) => {
      return readableDate(params.row.scheduledAt);
    },
  },
  {
    field: "confirmedTime",
    headerName: "Time",
    width: 90,
    sortable: true,
  },
  {
    field: "orderNumber",
    headerName: "External ID",
    width: 100,
    sortable: true,
  },
  {
    field: "id",
    headerName: "Internal ID",
    width: 100,
    sortable: true,
  },
  {
    field: "agents.name",
    headerName: "Agent",
    width: 120,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.pickupAgent?.agent?.name;
    },
  },
  {
    field: "itads.name",
    headerName: "ITAD",
    width: 120,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.pickupItad?.itad?.name;
    },
  },
  {
    field: "coordinators.firstName",
    headerName: "Coordinator",
    width: 100,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.revivnCoordinator?.firstName;
    },
  },
  {
    field: "organizations.name",
    headerName: "Company",
    width: 200,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.organization?.name;
    },
  },
  {
    field: "location",
    headerName: "Location",
    minWidth: 450,
    flex: 1,
    sortable: false,
    filterable: false,
    valueGetter: ({ row: { location } }: GridValueGetterParams<Pickup, Pickup>) => {
      return (
        location && `${location.address || ""} ${location.city || ""} ${location.state || ""} ${location.zipcode || ""}`
      );
    },
  },
  {
    field: "state",
    headerName: "State",
    width: 250,
    sortable: true,
    renderCell({ row: { state } }: GridRenderCellParams<Pickup>) {
      return <PickupStateChip pickupState={state} />;
    },
  },
  {
    field: "freighters.name",
    headerName: "Freighter",
    width: 80,
    filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    valueGetter: (params: GridValueGetterParams<Pickup, Pickup>) => {
      return params.row.freighter?.name;
    },
  },
  {
    field: "rescheduleInfo",
    headerName: "Reschedule Info",
    width: 200,
    sortable: false,
    renderCell: (params: GridRenderCellParams<Pickup>) => {
      const pickup = params.row;

      if (pickup.state !== "reschedule_pending" && !pickup.rescheduledFrom) {
        return "-";
      }

      const rescheduleDetails = [
        pickup.rescheduledFrom && `From: ${readableDate(pickup.rescheduledFrom)}`,
        pickup.rescheduledBy && `By: ${pickup.rescheduledBy.email}`,
        pickup.rescheduledReason && `Reason: ${pickup.rescheduledReason}`,
        pickup.reschedulePendingAt && `Date: ${new Date(pickup.reschedulePendingAt).toLocaleDateString()}`,
      ].filter(Boolean);

      return (
        <Box sx={{ fontSize: "12px", lineHeight: 1.2 }}>
          {rescheduleDetails.map((detail, index) => (
            <div key={index}>{detail}</div>
          ))}
        </Box>
      );
    },
  },
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    renderCell: (params) => <CustomDetailPanelToggle id={params.id} value={params.value} />,
  },
];

export default function LogisticsPickups() {
  const { pickups, count, confirmPickup, cancelPickup, reschedulePickup } = useLogisticsPickups();

  const searchRef = useRef<HTMLInputElement | null>(null);

  const pageState = useServerSideDataGridParams(pickups);

  const getDetailPanelContent = useCallback(
    ({ row: pickup }: GridRowParams<Pickup>) => (
      <LogisticsPickupRow
        pickup={pickup}
        confirmPickup={confirmPickup}
        cancelPickup={cancelPickup}
        reschedulePickup={reschedulePickup}
      />
    ),
    [cancelPickup, confirmPickup, reschedulePickup],
  );

  const onTabChange = useCallback(() => {
    if (searchRef.current) {
      searchRef.current.value = "";
    }
  }, []);

  const { enabled } = useFeatureFlag("logistics_pickups_detail");

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Logistics" }]} />
      <PageTitle>Pickups</PageTitle>
      <PageSubtitle>
        View details for the pickups that have been scheduled in the past, need approval, or are confirmed and upcoming.
      </PageSubtitle>
      <PageActions>
        <Button component={RouterLink} variant="contained" to="/logistics/schedule-pickup" startIcon={<Add />}>
          schedule a pickup
        </Button>
      </PageActions>
      <PagePaperPreamble>
        <LogisticsPickupTabs onTabChange={onTabChange} />
      </PagePaperPreamble>

      <DataGridPro
        autoHeight={false}
        sx={{ background: "background.paper" }}
        rows={pickups}
        columns={enabled ? newColumns : columns}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => "auto"}
        {...pageState}
        pageSize={pageState.pageSize || 25}
        rowsPerPageOptions={[25, 50, 100]}
        filterMode="server"
        rowCount={count}
        components={{
          Toolbar: CustomToolbar,
        }}
        initialState={{
          pinnedColumns: { right: [GRID_DETAIL_PANEL_TOGGLE_FIELD] },
        }}
      />
    </FullPagePaperLayout>
  );
}
